import { ROLES } from '../../../../constants';
import { resolveRoleIconVariant } from './createStaffRoleIconUtils';

/** Display order for Create Staff role cards (matches product layout). */
const CREATE_STAFF_ROLE_DISPLAY_ORDER = {
  'super-admin': 0,
  'system-admin': 1,
  'attorney': 2,
  'facility-admin': 3,
  'facility-staff': 4,
  'staff-business': 4,
  'messaging-only': 5,
  'default-muted': 100,
};

export function isSuperAdminRole(role) {
  if (!role) return false;
  return role.roleName === ROLES.SUPER_ADMIN || role.displayName === ROLES.SUPER_ADMIN;
}

export function isSystemAdminRole(role) {
  if (!role) return false;
  return role.roleName === ROLES.SYSTEM_ADMIN || role.displayName === ROLES.SYSTEM_ADMIN;
}

/** Super Admin / System Admin targets skip the optional permission step (not applicable or all effective). */
export function isPermissionSecondStepSkippedForTargetRole(role) {
  return isSuperAdminRole(role) || isSystemAdminRole(role);
}

/**
 * Whether create-staff shows a second step for role-scoped permission toggles.
 * @param {boolean} canManageCrossUserPermissionOverrides — viewer is Super/APDS Admin or System Admin
 */
export function shouldShowPermissionSecondStep(canManageCrossUserPermissionOverrides, selectedRole) {
  return (
    Boolean(canManageCrossUserPermissionOverrides) && !isPermissionSecondStepSkippedForTargetRole(selectedRole)
  );
}

/**
 * Sparse list for create payload field {@code staffPermissionToggles}.
 * Only rows where desired state differs from {@code grantedByRole}.
 */
export function buildSparsePermissionTogglesForCreate(catalog, toggleState) {
  if (!catalog || !catalog.length || !toggleState) return [];
  return catalog
    .filter((p) => toggleState[p.permissionId] !== p.grantedByRole)
    .map((p) => ({ permissionId: p.permissionId, enabled: Boolean(toggleState[p.permissionId]) }));
}

/**
 * Fixed Janus Feeds catalog order (Roles and Permissions).
 * `feed_conversation_update` is legacy alias for edit; kept next to `feed_update`.
 * Rows not in this list sort after, by display/name.
 */
export const FEEDS_PERMISSION_CATALOG_ORDER = [
  'feed_create',
  'feed_update',
  'feed_conversation_update',
  'feed_announcement_view_all',
  'feed_announcement_create',
  'feed_attachment_create',
  'feed_audit',
];

function sortIndexForFeedsPermission(item) {
  const key = item?.permissionName ?? item?.permission_name ?? '';
  const i = FEEDS_PERMISSION_CATALOG_ORDER.indexOf(key);
  if (i !== -1) return i;
  return FEEDS_PERMISSION_CATALOG_ORDER.length;
}

/** Stable-sorts role or staff permission rows from Janus for the Feeds UI. */
export function sortRolePermissionCatalog(list) {
  if (!list?.length) return [];
  return [...list].sort((a, b) => {
    const d = sortIndexForFeedsPermission(a) - sortIndexForFeedsPermission(b);
    if (d !== 0) return d;
    const na = String(
      a?.permissionDisplayName ?? a?.permission_display_name ?? a?.permissionName ?? a?.permission_name ?? ''
    ).toLowerCase();
    const nb = String(
      b?.permissionDisplayName ?? b?.permission_display_name ?? b?.permissionName ?? b?.permission_name ?? ''
    ).toLowerCase();
    return na.localeCompare(nb);
  });
}

export const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  displayFirstName: '',
  displayLastName: '',
  email: '',
  password: '',
  selectedRoleId: null,
  selectedRole: null,
  selectedLocations: [],
};

export const ALL_TOUCHED_KEYS = {
  firstName: true,
  lastName: true,
  displayFirstName: true,
  displayLastName: true,
  email: true,
  password: true,
  role: true,
  locations: true,
};

function sortRolesForCreateStaffDisplay(list) {
  if (!list || list.length <= 1) return list || [];
  return [...list].sort((a, b) => {
    const variantA = resolveRoleIconVariant(a);
    const variantB = resolveRoleIconVariant(b);
    const orderA = CREATE_STAFF_ROLE_DISPLAY_ORDER[variantA] ?? 100;
    const orderB = CREATE_STAFF_ROLE_DISPLAY_ORDER[variantB] ?? 100;
    if (orderA !== orderB) return orderA - orderB;
    const labelA = (a.displayName || a.roleName || '').toLowerCase();
    const labelB = (b.displayName || b.roleName || '').toLowerCase();
    return labelA.localeCompare(labelB);
  });
}

export function applySuperAdminAndFilters(fetchedRoles, isSuperAdmin) {
  if (isSuperAdmin) {
    const sorted = sortRolesForCreateStaffDisplay(fetchedRoles || []);
    const superAdminRole = {
      roleId: -1,
      roleName: ROLES.SUPER_ADMIN,
      displayName: ROLES.SUPER_ADMIN,
      roleType: ROLES.SUPER_ADMIN,
    };
    return [superAdminRole, ...sorted];
  }
  const filtered = (fetchedRoles || []).filter((role) => {
    const roleType = role.roleType || role.roleName || role.displayName || '';
    return roleType !== ROLES.SUPER_ADMIN && roleType !== ROLES.ATTORNEY;
  });
  return sortRolesForCreateStaffDisplay(filtered);
}

/**
 * Hides the System Admin assignee role when {@code createStaffV2Enabled} is false, or when the viewer
 * is not Super/System Admin (v2 on).
 * @param {Array} roles — typically output of {@link applySuperAdminAndFilters}
 * @param {boolean} canViewSystemAdminRole — viewer is {@code isSuperAdmin || isSystemAdmin}
 * @param {boolean} [createStaffV2Enabled=true] — when false, System Admin is never listed (legacy UI).
 */
export function filterSystemAdminRoleForViewer(roles, canViewSystemAdminRole, createStaffV2Enabled = true) {
  if (!createStaffV2Enabled) {
    return (roles || []).filter((r) => !isSystemAdminRole(r));
  }
  if (canViewSystemAdminRole) return roles || [];
  return (roles || []).filter((r) => !isSystemAdminRole(r));
}

// Same pattern as legacy manage-staff dialog (high complexity by design).
/* eslint-disable sonarjs/regex-complexity -- parity with legacy email validation */
const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
/* eslint-enable sonarjs/regex-complexity */

// Must contain at least one special character (same character set as legacy form).
// eslint-disable-next-line sonarjs/duplicates-in-character-class, no-useless-escape -- intentional set; keep legacy behavior
const PASSWORD_SPECIAL_RE = /[\s~`!@#$%^&*+=\-[\]\\';,/{}|\\":<>?()\._]/;

export function validateEmail(email) {
  return EMAIL_REGEX.test(email);
}

export function validateForm(values, locations) {
  const newErrors = {};
  const superAdminSelected = isSuperAdminRole(values.selectedRole);

  if (!values.firstName) {
    newErrors.firstName = 'First name is required';
  }
  if (!values.lastName) {
    newErrors.lastName = 'Last name is required';
  }
  if (!values.displayFirstName) {
    newErrors.displayFirstName = 'Display first name is required';
  }
  if (!values.displayLastName) {
    newErrors.displayLastName = 'Display last name is required';
  }

  if (!values.email) {
    newErrors.email = 'Email is required';
  } else if (!validateEmail(values.email)) {
    newErrors.email = 'Email is not valid';
  }

  /* eslint-disable sonarjs/no-hardcoded-passwords -- validation UI strings, not credentials */
  if (!values.password) {
    newErrors.password = 'Password is required';
  } else if (values.password.length < 12) {
    newErrors.password = 'Password must be 12 characters long';
  } else if (/^[a-z]*$/.test(values.password)) {
    newErrors.password = 'Password must be of mix case';
  } else if (!PASSWORD_SPECIAL_RE.test(values.password)) {
    newErrors.password = 'Password must include special character';
  }
  /* eslint-enable sonarjs/no-hardcoded-passwords */

  if (!values.selectedRoleId) {
    newErrors.role = 'Please select a role';
  }

  if (!superAdminSelected && values.selectedRoleId) {
    if (!locations || locations.length === 0) {
      newErrors.locations = 'Please select at least one location';
    }
  }

  return newErrors;
}
